/* eslint complexity: ["error", {"max": 15, "variant": "modified"}] */
import {
  type Bet1X2,
  type BetChallenge,
  type BetFulltraff,
  type BetNumboards,
  type BetScore,
  type IBet,
  type IBoard,
  Wager,
} from '@svenskaspelab/nova-payment-sport/wager';
import type { Role } from '@svs/nova-core/nextjs/auth';

import type { CouponIndex } from '../../../../schema/coupon/entry.js';
import type { WalletType } from '../../../../schema/frameworks/entry.js';
import type { Commission } from '../../../../schema/input/entry.js';
import type { ActivatedDrawId } from '../../../../schema/marketplace/entry.js';
import type { RSYSNum, USYSNum } from '../../../../schema/system/entry.js';
import { CouponEntity, type ICouponEntity, type IDefinition } from '../../../domain/entry.js';

export type SystemNumber = RSYSNum | USYSNum;

type WagerData = {
  wallet: WalletType | ActivatedDrawId;
  couponIndex: CouponIndex;
  paymentContext: {
    role?: Role;
    commissionPercent?: Commission;
    activatedDrawId?: ActivatedDrawId;
  };
};

type ExternalWagerData = {
  wallet: WalletType | ActivatedDrawId;
  paymentContext: {
    activatedDrawId?: ActivatedDrawId;
  };
};

type ExternalChunkedWagerData = ExternalWagerData & {
  definition: IDefinition;
};

function getSystemNumber(couponEntity: ICouponEntity): SystemNumber | undefined {
  const { systemType, systemNumber } = couponEntity;
  let system: SystemNumber | undefined = undefined;

  if (systemType === 'RSYS' || systemType === 'USYS') {
    system = systemNumber;
  }

  return system;
}

function addBasicBetInfo(bet: IBet<IBoard>, { couponEntity }: { couponEntity: ICouponEntity }): void {
  const numberOfDraws = couponEntity.drawCount;

  bet.setDrawNumber(couponEntity.drawNumber);
  bet.setRowPrice(couponEntity.betAmount);

  if (numberOfDraws > 1) {
    bet.setNumberOfDraws(numberOfDraws);
  }
}

function create1x2Bet(bet: Bet1X2, { couponEntity }: { couponEntity: ICouponEntity }): void {
  addBasicBetInfo(bet, { couponEntity });

  const systemNumber = getSystemNumber(couponEntity);

  if (couponEntity.systemType === 'REDUCERA_FRITT') {
    bet.addBetProperty(couponEntity.systemType);
  }

  for (const board of couponEntity.getBoards()) {
    if (!board.isComplete) {
      throw new Error(`Board ${board.boardIndex} is not valid`);
    }

    bet.addBoard((wagerBoard) => {
      for (const eventOutcomes of board.outcomes) {
        wagerBoard.addOutcomes(...eventOutcomes);
      }

      if (board.quickPickVariant) {
        wagerBoard.addBoardProperty(board.quickPickVariant);
      }

      if (systemNumber) {
        wagerBoard.setSystemNumber(systemNumber);
        for (const [index, isOutcome] of board.mOutcomes.entries()) {
          if (isOutcome) {
            wagerBoard.addMathHedge(index + 1);
          }
        }
        for (const [index, outcome] of board.uOutcomes.entries()) {
          if (outcome !== null) {
            wagerBoard.addUOutcome(index + 1, outcome);
          }
        }
      }
    });
  }
}

function createNumBoardsBet(bet: BetNumboards, { couponEntity }: { couponEntity: ICouponEntity }): void {
  addBasicBetInfo(bet, { couponEntity });
  // TODO: add more when maltipset is implemented
}

function createScoreBet(bet: BetScore, { couponEntity }: { couponEntity: ICouponEntity }): void {
  addBasicBetInfo(bet, { couponEntity });

  for (const board of couponEntity.getBoards()) {
    if (!board.isComplete) {
      continue;
    }

    bet.addBoard((wagerBoard) => {
      for (const eventOutcomes of board.outcomes) {
        wagerBoard.addOutcomes(...eventOutcomes);
      }

      if (board.quickPickVariant) {
        wagerBoard.addBoardProperty(board.quickPickVariant);
      }
    });
  }
}

function createFulltraffBet(bet: BetFulltraff, { couponEntity }: { couponEntity: ICouponEntity }): void {
  addBasicBetInfo(bet, { couponEntity });
  // TODO: add more when fulltraff is implemented
}

function createChallengeBet(bet: BetChallenge, { couponEntity }: { couponEntity: ICouponEntity }): void {
  addBasicBetInfo(bet, { couponEntity });
  // TODO: add more when challenge is implemented
}

function createSportkryssBet(wager: Wager, { couponEntity }: { couponEntity: ICouponEntity }): void {
  const numberOfDraws = couponEntity.drawCount;

  const currentSportKryssFactor = couponEntity.sportkryssFactor;
  if (currentSportKryssFactor > 0) {
    wager.addBet('Sportkrysset', (bet) => {
      bet.setRowPrice(currentSportKryssFactor);

      if (numberOfDraws > 1) {
        bet.setNumberOfDraws(numberOfDraws);
      }

      const currentSportkryssNumbers = couponEntity.sportkryssNumber;
      bet.addBoard((wagerBoard) => {
        const sportKryssOutcomes = currentSportkryssNumbers.split('').map((outcome) => Number.parseInt(outcome, 10));
        for (const outcome of sportKryssOutcomes) {
          wagerBoard.addOutcomes([outcome]);
        }
      });
    });
  }
}

/**
 * Creates an ITS wager from the Redux state for a given coupon id.
 *
 * @param couponEntity Coupon state
 * @param root0 Data to create the wager
 * @param root0.wallet What kind of wallet to pay with
 * @param root0.couponIndex Optional index of the coupon in the wallet
 * @param root0.paymentContext Payment context such as user role
 * @returns ITS Wager
 * @throws {Error} if the place bet could not be initialized
 */
export function createWager(couponEntity: ICouponEntity, { wallet, couponIndex, paymentContext }: WagerData): Wager {
  const { systemType, product, sportkryssFactor: currentSportKryssFactor } = couponEntity;

  const { role, commissionPercent, activatedDrawId } = paymentContext;
  const isMarketplaceWallet = typeof wallet === 'number' || (role === 'Retailer' && activatedDrawId !== undefined);
  const wager = new Wager();
  const isMarketplaceWithReduceraFritt = isMarketplaceWallet && systemType === 'REDUCERA_FRITT';
  const hasSportKryss = !isMarketplaceWithReduceraFritt && currentSportKryssFactor > 0;

  const selectedActivatedDrawId = wallet && typeof wallet === 'number' ? wallet : activatedDrawId;
  if (isMarketplaceWallet && selectedActivatedDrawId !== undefined) {
    wager.setActivatedDrawId(selectedActivatedDrawId);
    wager.setCustomer(role === 'Retailer' ? 'Retailer' : 'Group');
    // Is possible for commissionPercent to be 0, therefore we need to check for undefined
    commissionPercent !== undefined && wager.setCommissionPercent(commissionPercent);
  }

  if (typeof couponIndex === 'number') {
    wager.setCouponIndex(couponIndex);
  }

  if (hasSportKryss) {
    createSportkryssBet(wager, { couponEntity });
  }

  switch (product) {
    case 'Powerplay':
    case 'Europatipset':
    case 'Stryktipset':
    case 'Topptipset':
    case 'TopptipsetStryk':
    case 'TopptipsetExtra':
      wager.addBet(product, (bet) => {
        create1x2Bet(bet, { couponEntity });
      });
      break;
    case 'Maltipset':
      wager.addBet(product, (bet) => {
        createNumBoardsBet(bet, { couponEntity });
      });
      break;
    case 'Bomben':
    case 'Matchen':
      wager.addBet(product, (bet) => {
        createScoreBet(bet, { couponEntity });
      });
      break;
    case 'Fulltraff':
      wager.addBet(product, (bet) => {
        createFulltraffBet(bet, { couponEntity });
      });
      break;
    case 'Challenge':
      wager.addBet(product, (bet) => {
        createChallengeBet(bet, { couponEntity });
      });
      break;
    default:
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      throw new Error(`Product ${product} is not supported`);
  }

  return wager;
}

function create1x2SrowBet(
  bet: Bet1X2,
  { coupons, firstCoupon }: { coupons: ICouponEntity[]; firstCoupon: ICouponEntity }
): void {
  addBasicBetInfo(bet, { couponEntity: firstCoupon });
  for (const coupon of coupons) {
    for (const board of coupon.getBoards()) {
      if (!board.isComplete) {
        throw new Error(`Board ${board.boardIndex} is not valid`);
      }

      bet.addBoard((wagerBoard) => {
        for (const eventOutcomes of board.outcomes) {
          wagerBoard.addOutcomes(...eventOutcomes);
        }

        if (board.quickPickVariant) {
          wagerBoard.addBoardProperty(board.quickPickVariant);
        }
      });
    }
  }
}

type SingleRowsWagerData = {
  wallet: WalletType | ActivatedDrawId;
  paymentContext: {
    role?: Role;
    commissionPercent?: Commission;
    activatedDrawId?: ActivatedDrawId;
  };
};

/**
 * Creates a single rows (SROW) wager from multiple coupons.
 *
 * Single rows wagering allows combining boards from multiple coupons into a single wager,
 * where each board represents one complete row. This is typically used for poolbetting
 * products like Stryktipset and Europatipset.
 *
 * @param coupons Array of coupon entities to combine into a single wager
 * @param root0 props
 * @param root0.wallet wallet used for singlerows
 * @param root0.paymentContext Payment context including user role and activated draw ID
 * @returns ITS Wager containing all boards from the provided coupons
 * @throws {Error} if no coupons are provided
 * @throws {Error} if any board is incomplete
 * @throws {Error} if the product is not supported for single rows wagering
 */
export function createSrowWager(coupons: ICouponEntity[], { wallet, paymentContext }: SingleRowsWagerData): Wager {
  const [firstCoupon] = coupons;
  if (!firstCoupon) {
    throw new Error('At least one coupon is required to create a single rows wager');
  }

  const { role, commissionPercent, activatedDrawId } = paymentContext;
  const wager = new Wager();
  const { product, sportkryssFactor: currentSportKryssFactor } = firstCoupon;

  const hasSportKryss = currentSportKryssFactor > 0;

  const isMarketplaceWallet = typeof wallet === 'number' || (role === 'Retailer' && activatedDrawId !== undefined);
  const selectedActivatedDrawId = wallet && typeof wallet === 'number' ? wallet : activatedDrawId;
  if (isMarketplaceWallet && selectedActivatedDrawId !== undefined) {
    wager.setActivatedDrawId(selectedActivatedDrawId);
    wager.setCustomer(role === 'Retailer' ? 'Retailer' : 'Group');
    // Is possible for commissionPercent to be 0, therefore we need to check for undefined
    commissionPercent !== undefined && wager.setCommissionPercent(commissionPercent);
  }

  if (hasSportKryss) {
    createSportkryssBet(wager, { couponEntity: firstCoupon });
  }

  switch (product) {
    case 'Powerplay':
    case 'Europatipset':
    case 'Stryktipset':
    case 'Topptipset':
    case 'TopptipsetStryk':
    case 'TopptipsetExtra':
      wager.addBet(product, (bet) => {
        create1x2SrowBet(bet, { coupons, firstCoupon });
      });
      break;
    default:
      throw new Error(`Product ${product} is not supported`);
  }

  return wager;
}

function createExternalBet(bet: Bet1X2, { couponEntity }: { couponEntity: ICouponEntity }): void {
  addBasicBetInfo(bet, { couponEntity });

  bet.addBetProperty('EGNARADER');

  for (const board of couponEntity.getBoards()) {
    if (!board.isComplete) {
      throw new Error(`Board ${board.boardIndex} is not valid`);
    }

    bet.addBoard((wagerBoard) => {
      for (const eventOutcomes of board.outcomes) {
        wagerBoard.addOutcomes(...eventOutcomes);
      }
    });
  }
}

/**
 * Creates an external wager from a given coupon.
 *
 * @param couponEntity Coupon state
 * @param root0 Data to create the wager
 * @param root0.wallet What kind of wallet to pay with
 * @param root0.paymentContext Payment context including activated draw ID
 * @returns ITS Wager
 * @throws {Error} if the place bet could not be initialized
 */
export function createExternalWager(couponEntity: ICouponEntity, { wallet, paymentContext }: ExternalWagerData): Wager {
  const { activatedDrawId } = paymentContext;
  const { product } = couponEntity;

  const wager = new Wager();

  const selectedActivatedDrawId = typeof wallet === 'number' ? wallet : activatedDrawId;
  const isMarketplaceWallet = selectedActivatedDrawId !== undefined;

  if (isMarketplaceWallet) {
    wager.setActivatedDrawId(selectedActivatedDrawId);
    wager.setCustomer('Group');
  }

  switch (product) {
    case 'Powerplay':
    case 'Europatipset':
    case 'Stryktipset':
    case 'Topptipset':
    case 'TopptipsetStryk':
    case 'TopptipsetExtra':
      wager.addBet(product, (bet) => {
        createExternalBet(bet, { couponEntity });
      });
      break;
    default:
      throw new Error(`Product ${product} is not supported`);
  }
  return wager;
}

function getExternalChunkSize({
  couponEntity,
  definition,
  wallet,
  paymentContext,
}: {
  couponEntity: ICouponEntity;
  definition: IDefinition;
  wallet: WalletType | ActivatedDrawId;
  paymentContext: ExternalWagerData['paymentContext'];
}): number {
  const { selectedSystem } = couponEntity.model;
  const selectedActivatedDrawId = typeof wallet === 'number' ? wallet : paymentContext.activatedDrawId;
  const isMarketplaceGroup = selectedActivatedDrawId !== undefined;

  if (selectedSystem === 'MSYS') {
    return isMarketplaceGroup
      ? definition.model.externalChunkSize.MSYS_MARKETPLACE_GROUP
      : definition.model.externalChunkSize.MSYS;
  }

  if (selectedSystem === 'SROW') {
    return isMarketplaceGroup
      ? definition.model.externalChunkSize.SROW_MARKETPLACE_GROUP
      : definition.model.externalChunkSize.SROW;
  }

  throw new Error(`Unsupported external system type: ${selectedSystem}`);
}

/**
 * Creates multiple external wagers by splitting boards into chunks.
 *
 * @param couponEntity Coupon state
 * @param root0 Data to create the wagers
 * @param root0.wallet What kind of wallet to pay with
 * @param root0.paymentContext Payment context including activated draw ID
 * @param root0.definition Product definition containing external chunk sizes
 * @returns Array of external wagers where each wager contains a chunk of boards
 * @throws {Error} if the external system type is not supported
 */
export function createChunkedExternalWagers(
  couponEntity: ICouponEntity,
  { wallet, paymentContext, definition }: ExternalChunkedWagerData
): Wager[] {
  const wagers: Wager[] = [];
  const couponModel = couponEntity.model;
  const chunkSize = getExternalChunkSize({ couponEntity, definition, wallet, paymentContext });

  for (let offset = 0; offset < couponModel.boards.length; offset += chunkSize) {
    const chunkedCouponEntity = new CouponEntity(
      {
        ...couponModel,
        boards: couponModel.boards.slice(offset, offset + chunkSize),
      },
      couponEntity.couponIndex,
      definition
    );

    wagers.push(createExternalWager(chunkedCouponEntity, { wallet, paymentContext }));
  }

  return wagers;
}
